import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { AlertService } from '../../../_services/alert.service';
import { DeliveryValidationService } from '../../../_services/delivery-validation.service';
import { ProductService, ProductPickerItem } from '../../../_services/product.service';
import { PurchaseReceiptsComponent } from '../purchase-receipts/purchase-receipts.component';
import { ProductPickerComponent } from '../../../../Shared/_components/product-picker/product-picker.component';
import { computeDispatchDate, dispatchUrgencyClass, formatDeliveryTerms } from '../dispatch-date';
import { DISPATCH_GROUPS, DispatchGroupOption, dispatchGroupOption } from '../dispatch-groups';
import { PaginatorPagesDirective } from '../../../../Shared/_directives/paginator-pages.directive';

type QueueTab = 'PROCESSING' | 'COMPLETED' | 'DELAYED' | 'VOID' | 'CANCELLED';

@Component({
    selector: 'app-delivery-validation',
    imports: [PaginatorPagesDirective,
        CommonModule,
        FormsModule,
        MainLayoutComponent,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatMenuModule,
        DatePipe,
        PurchaseReceiptsComponent,
        ProductPickerComponent,
    ],
    templateUrl: './delivery-validation.component.html',
    styleUrl: './delivery-validation.component.css'
})
export class DeliveryValidationComponent implements OnInit {
  // Queue of orders awaiting dispatch - the default view, so the team
  // doesn't need to already know an order number to find work to do.
  processingOrders: any[] = [];
  queueLoading = false;
  queueCount = 0;
  exporting = false;
  queuePageNumber = 0;
  queueItemsPerPage = 10;

  // Mutually exclusive queue views - see the matching tab logic in
  // DeliveryValidationService.getQueue.
  activeTab: 'PROCESSING' | 'COMPLETED' | 'DELAYED' | 'VOID' | 'CANCELLED' | 'RECEIPTS' = 'PROCESSING';
  tabs: { value: 'PROCESSING' | 'COMPLETED' | 'DELAYED' | 'VOID' | 'CANCELLED' | 'RECEIPTS'; label: string; icon: string }[] = [
    { value: 'PROCESSING', label: 'Processing', icon: 'inventory_2' },
    { value: 'COMPLETED', label: 'Completed', icon: 'check_circle' },
    { value: 'DELAYED', label: 'Delayed', icon: 'report_problem' },
    { value: 'VOID', label: 'Void', icon: 'block' },
    { value: 'CANCELLED', label: 'Cancelled', icon: 'cancel' },
    { value: 'RECEIPTS', label: 'Incoming Stocks', icon: 'local_shipping' },
  ];

  // Filters - all narrow the same queue table, matched against product
  // name/SKU/barcode, order number, postcode, delivery method and dispatch
  // group. Every field accepts multiple values - Product/Order No./
  // Postcode as a typed comma-separated list, Method/Dispatch Group as a
  // checkbox multi-select - the backend ORs each value.
  filterSearch = '';
  filterOrderNo = '';
  filterPostCode = '';
  filterDeliveryMethods: string[] = [];
  filterDispatchGroups: string[] = [];

  // Order-date range, same widget/presets as the Reporting Dashboard - no
  // preset is active by default (unlike the dashboard, which defaults to
  // "1D") since this queue's whole point is surfacing the backlog, not
  // just today's orders. Picking a preset only fills the From/To fields;
  // like every other filter here, it still needs Filter clicked to apply.
  // Which date the range applies to - switchable, since "when was it
  // ordered" and "when is it due out" are different questions for a
  // dispatch team.
  dateField: 'ORDER' | 'PAID' | 'VALIDATED' | 'DISPATCH_BY' = 'ORDER';
  readonly dateFields: { key: 'ORDER' | 'PAID' | 'VALIDATED' | 'DISPATCH_BY'; label: string; hint: string; icon: string }[] = [
    { key: 'ORDER', label: 'Order date', hint: 'When the customer placed the order', icon: 'shopping_cart' },
    { key: 'PAID', label: 'Paid date', hint: 'When payment cleared', icon: 'payments' },
    { key: 'DISPATCH_BY', label: 'Dispatch by', hint: 'When it has to go out (paid date + delivery days)', icon: 'event' },
    { key: 'VALIDATED', label: 'Validated date', hint: 'When the dispatch team checked items off', icon: 'fact_check' },
  ];
  datePreset: string | null = null;
  filterDtFrom = '';
  filterDtTo = '';
  readonly datePresets: { key: string; label: string }[] = [
    { key: '1d', label: '1D' },
    { key: '7d', label: '7D' },
    { key: '30d', label: '30D' },
    { key: '90d', label: '90D' },
    { key: 'month', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'ytd', label: 'YTD' },
  ];

  // Search-as-you-type suggestions for the Product filter, same picker as
  // Purchase Orders / Stock Scrap.
  products: ProductPickerItem[] = [];

  deliveryMethodOptions = [
    { value: 'STANDARD', label: 'Standard' },
    { value: 'EXPRESS', label: 'Express' },
    { value: 'NEXTDAY', label: 'Next Day' },
    { value: 'COLLECTION', label: 'Collection' },
  ];

  dispatchGroupOptions = DISPATCH_GROUPS;
  dispatchGroupFilterOptions = [
    { value: 'UNASSIGNED', label: 'Unassigned' },
    ...DISPATCH_GROUPS,
  ];

  // Selection is scoped to the current page - cleared on every reload
  // (filter change, pagination, refresh) rather than tracked across pages.
  selectedOrderIds = new Set<number>();
  bulkAssigning = false;
  // True once "Select all N orders" has pulled in every matching order id
  // (across pages, not just the visible one) - selection then survives
  // pagination and only resets when the tab/filters change.
  allMatchingSelected = false;
  selectingAll = false;
  selectFirstCount: number | null = null;

  constructor(
    private deliveryValidationService: DeliveryValidationService,
    private productService: ProductService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // ?tab=RECEIPTS (e.g. back from a container's page) opens that tab.
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab && this.tabs.some((t) => t.value === tab)) this.activeTab = tab as typeof this.activeTab;
    this.loadQueue();
  }

  // The picker list (hundreds of products with stock joins) is only
  // needed once someone actually goes to search, so it's fetched on first
  // focus rather than competing with the queue on page load.
  private productsRequested = false;

  onProductSearchFocus(): void {
    if (this.productsRequested) return;
    this.productsRequested = true;
    this.productService.getProductPicker().subscribe({
      next: (products) => (this.products = products),
      // Non-fatal: the dropdown just stays empty, typing + Filter still works.
      error: () => (this.products = []),
    });
  }

  // Orders per tab under the current filters, shown on the tab bar.
  tabCounts: Record<string, number> = {};

  private loadTabCounts() {
    this.deliveryValidationService
      .getQueueCounts({
        search: this.filterSearch || undefined,
        orderNo: this.filterOrderNo || undefined,
        postCode: this.filterPostCode || undefined,
        deliveryMethod: this.filterDeliveryMethods.join(',') || undefined,
        dispatchGroup: this.filterDispatchGroups.join(',') || undefined,
        dtFrom: this.filterDtFrom || undefined,
        dtTo: this.filterDtTo || undefined,
        dateField: this.dateField,
      })
      .subscribe({
        next: (counts) => (this.tabCounts = counts ?? {}),
        // Non-fatal: the tabs just show without numbers.
        error: () => {},
      });
  }

  onReceiptsChanged(): void {
    this.loadTabCounts();
  }

  // Counts don't depend on the page, so paging skips re-fetching them.
  loadQueue(refreshCounts = true) {
    if (refreshCounts) this.loadTabCounts();
    if (this.activeTab === 'RECEIPTS') return;
    this.queueLoading = true;
    this.deliveryValidationService
      .getQueue({
        status: 'PROCESSING',
        tab: this.activeTab as QueueTab,
        pageIndex: this.queuePageNumber,
        pageSize: this.queueItemsPerPage,
        search: this.filterSearch || undefined,
        orderNo: this.filterOrderNo || undefined,
        postCode: this.filterPostCode || undefined,
        deliveryMethod: this.filterDeliveryMethods.join(',') || undefined,
        dispatchGroup: this.filterDispatchGroups.join(',') || undefined,
        dtFrom: this.filterDtFrom || undefined,
        dtTo: this.filterDtTo || undefined,
        dateField: this.dateField,
      })
      .subscribe({
        next: (response) => {
          this.processingOrders = response?.orders ?? [];
          this.queueCount = response?.count ?? 0;
          this.queueLoading = false;
          if (!this.allMatchingSelected) this.selectedOrderIds.clear();
        },
        error: (error) => {
          this.queueLoading = false;
          this.alertService.alert(error?.error?.message || 'Failed to load orders', 'error');
        },
      });
  }

  // Pick sheet: re-queries the current tab + filters (not just the visible
  // page) and writes one row per order, with multiple items joined into a
  // single cell (products and quantities in matching order).
  exportToExcel() {
    if (this.exporting || !this.queueCount) return;
    this.exporting = true;

    this.deliveryValidationService
      .getQueue({
        status: 'PROCESSING',
        tab: this.activeTab as QueueTab,
        pageIndex: 0,
        pageSize: Math.min(this.queueCount, 5000),
        search: this.filterSearch || undefined,
        orderNo: this.filterOrderNo || undefined,
        postCode: this.filterPostCode || undefined,
        deliveryMethod: this.filterDeliveryMethods.join(',') || undefined,
        dispatchGroup: this.filterDispatchGroups.join(',') || undefined,
        dtFrom: this.filterDtFrom || undefined,
        dtTo: this.filterDtTo || undefined,
        dateField: this.dateField,
      })
      .subscribe({
        next: async (response) => {
          try {
            await this.writePickSheet(response?.orders ?? []);
          } catch (e) {
            this.alertService.alert('Failed to build the Excel file', 'error');
          }
          this.exporting = false;
        },
        error: (error) => {
          this.exporting = false;
          this.alertService.alert(error?.error?.message || 'Failed to export orders', 'error');
        },
      });
  }

  // exceljs (not xlsx, which can't style cells or write rich text) so each
  // order's items can sit in one wrapped cell as "Name X qty" lines, with
  // the quantity picked out in a bold highlight colour. Loaded on demand -
  // it's large and only needed when someone exports.
  private async writePickSheet(orders: any[]) {
    const ExcelJS: any = await import('exceljs');
    const Workbook = (ExcelJS.default ?? ExcelJS).Workbook;
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet('Pick Sheet', { views: [{ state: 'frozen', ySplit: 1 }] });

    sheet.columns = [
      { header: 'Order Number', key: 'orderNo', width: 14 },
      { header: 'Customer Name', key: 'name', width: 22 },
      { header: 'Customer Delivery Email', key: 'email', width: 30 },
      { header: 'Postal Code', key: 'postCode', width: 12 },
      { header: 'Product Items', key: 'items', width: 80 },
      { header: 'Delivery Method', key: 'method', width: 16 },
      { header: 'Paid Date', key: 'paid', width: 20 },
      { header: 'Dispatch Date', key: 'dispatch', width: 16 },
    ];

    const header = sheet.getRow(1);
    header.height = 22;
    header.eachCell((cell: any) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF171717' } };
      cell.alignment = { vertical: 'middle' };
    });

    const textFont = { name: 'Calibri', size: 11, color: { argb: 'FF262626' } };
    const qtyFont = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFE11D48' } };

    for (const o of orders) {
      const items: any[] = o.OrderItems ?? [];
      const richText: any[] = [];
      items.forEach((item, i) => {
        const last = i === items.length - 1;
        richText.push({ text: `${item.productName} X `, font: textFont });
        richText.push({ text: String(item.quantity), font: qtyFont });
        if (!last) richText.push({ text: '\n', font: textFont });
      });

      const dispatch = this.dispatchDate(o);
      const row = sheet.addRow({
        orderNo: o.orderNo,
        name: this.customerName(o) === '-' ? '' : this.customerName(o),
        email: this.shippingAddress(o)?.email || '',
        postCode: this.shippingAddress(o)?.postCode || '',
        items: { richText },
        method: this.deliveryMethodLabel(o) || '',
        paid: o.paymentDate ? formatDate(o.paymentDate, 'dd MMM yyyy, HH:mm', 'en') : '',
        dispatch: dispatch ? formatDate(dispatch, 'dd MMM yyyy', 'en') : '',
      });
      row.height = Math.max(1, items.length) * 17 + 4;
      row.eachCell((cell: any) => {
        cell.alignment = { vertical: 'top', wrapText: true };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FFE5E5E5' } } };
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `pick-sheet-${this.activeTab.toLowerCase()}-${formatDate(new Date(), 'yyyy-MM-dd', 'en')}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }

  get queueTitle(): string {
    const titles: Record<string, string> = {
      PROCESSING: 'Orders Awaiting Dispatch',
      COMPLETED: 'Validated Orders',
      DELAYED: 'Delayed Orders',
      VOID: 'Void Orders',
      CANCELLED: 'Cancelled Deliveries',
    };
    return titles[this.activeTab] || 'Orders';
  }

  // The count pill at the top always said "awaiting dispatch" regardless
  // of which tab was open - showing "1 awaiting dispatch" while looking at
  // Completed orders was just wrong copy, not a real count bug.
  get queueCountLabel(): string {
    const labels: Record<string, string> = {
      PROCESSING: 'awaiting dispatch',
      COMPLETED: 'validated',
      DELAYED: 'delayed',
      VOID: 'void',
      CANCELLED: 'cancelled',
    };
    return labels[this.activeTab] || '';
  }

  switchTab(tab: 'PROCESSING' | 'COMPLETED' | 'DELAYED' | 'VOID' | 'CANCELLED' | 'RECEIPTS') {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.queuePageNumber = 0;
    this.resetSelection();
    // Incoming Stocks is its own panel (not an order list) - nothing to load here.
    if (tab !== 'RECEIPTS') this.loadQueue();
  }

  toggleSelect(order: any) {
    this.allMatchingSelected = false;
    if (this.selectedOrderIds.has(order.orderId)) {
      this.selectedOrderIds.delete(order.orderId);
    } else {
      this.selectedOrderIds.add(order.orderId);
    }
  }

  isSelected(order: any): boolean {
    return this.selectedOrderIds.has(order.orderId);
  }

  get allSelectedOnPage(): boolean {
    return (
      this.processingOrders.length > 0 &&
      this.processingOrders.every((o) => this.selectedOrderIds.has(o.orderId))
    );
  }

  toggleSelectAll() {
    this.allMatchingSelected = false;
    if (this.allSelectedOnPage) {
      this.selectedOrderIds.clear();
    } else {
      this.processingOrders.forEach((o) => this.selectedOrderIds.add(o.orderId));
    }
  }

  // Offered once the whole page is ticked and more orders match beyond it.
  get canSelectAllMatching(): boolean {
    return (
      (this.allSelectedOnPage || this.allMatchingSelected) &&
      this.queueCount > this.selectedOrderIds.size
    );
  }

  // Pulls every order id matching the current tab + filters (ids only, so
  // it stays cheap) - the queue is paginated, so the page alone can't say.
  selectAllMatching() {
    this.selectMatching(null);
  }

  // "Select first N" - the first N orders in list order (newest first),
  // continuing across pages, e.g. the first 25 of 30 when pages hold 10.
  selectFirst() {
    const n = Math.floor(Number(this.selectFirstCount));
    if (!n || n < 1) {
      this.alertService.alert('Enter how many orders to select', 'error');
      return;
    }
    this.selectMatching(n);
  }

  private selectMatching(limit: number | null) {
    this.selectingAll = true;
    this.deliveryValidationService
      .getQueueOrderIds({
        status: 'PROCESSING',
        tab: this.activeTab as QueueTab,
        pageIndex: 0,
        pageSize: 0,
        search: this.filterSearch || undefined,
        orderNo: this.filterOrderNo || undefined,
        postCode: this.filterPostCode || undefined,
        deliveryMethod: this.filterDeliveryMethods.join(',') || undefined,
        dispatchGroup: this.filterDispatchGroups.join(',') || undefined,
        dtFrom: this.filterDtFrom || undefined,
        dtTo: this.filterDtTo || undefined,
        dateField: this.dateField,
      })
      .subscribe({
        next: (ids) => {
          const all = ids ?? [];
          this.selectedOrderIds = new Set(limit ? all.slice(0, limit) : all);
          this.allMatchingSelected = true;
          this.selectingAll = false;
        },
        error: (error) => {
          this.selectingAll = false;
          this.alertService.alert(error?.error?.message || 'Failed to select orders', 'error');
        },
      });
  }

  clearSelection() {
    this.resetSelection();
  }

  private resetSelection() {
    this.allMatchingSelected = false;
    this.selectedOrderIds.clear();
  }

  // "Showing 1-10 of 30"
  get rangeLabel(): string {
    if (!this.processingOrders.length) return '';
    const start = this.queuePageNumber * this.queueItemsPerPage + 1;
    const end = start + this.processingOrders.length - 1;
    return `Showing ${start}-${end} of ${this.queueCount}`;
  }

  // Sets the same group on every selected order in one request, then
  // updates each affected row in place rather than reloading the whole
  // page.
  bulkAssignDispatchGroup(value: string) {
    const orderIds = Array.from(this.selectedOrderIds);
    if (!orderIds.length) return;

    this.bulkAssigning = true;
    this.deliveryValidationService.bulkSetDispatchGroup(orderIds, value).subscribe({
      next: (groups) => {
        const byOrderId = new Map(groups.map((g) => [g.orderId, g]));
        this.processingOrders.forEach((o) => {
          if (byOrderId.has(o.orderId)) {
            o.dispatchGroup = byOrderId.get(o.orderId);
          }
        });
        this.bulkAssigning = false;
        this.resetSelection();
        this.alertService.alert(`Dispatch group set for ${orderIds.length} order${orderIds.length === 1 ? '' : 's'}`, 'success');
      },
      error: (error) => {
        this.bulkAssigning = false;
        this.alertService.alert(error?.error?.message || 'Failed to update dispatch group', 'error');
      },
    });
  }

  applyFilters() {
    this.queuePageNumber = 0;
    this.resetSelection();
    this.loadQueue();
  }

  clearFilters() {
    this.filterSearch = '';
    this.filterOrderNo = '';
    this.filterPostCode = '';
    this.filterDeliveryMethods = [];
    this.filterDispatchGroups = [];
    this.datePreset = null;
    this.filterDtFrom = '';
    this.filterDtTo = '';
    this.dateField = 'ORDER';
    this.applyFilters();
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filterSearch ||
      this.filterOrderNo ||
      this.filterPostCode ||
      this.filterDeliveryMethods.length ||
      this.filterDispatchGroups.length ||
      this.filterDtFrom ||
      this.filterDtTo
    );
  }

  // Same preset math as the Reporting Dashboard - fills From/To, doesn't
  // query on its own (Filter still applies it, like every other field
  // here).
  get dateFieldIcon(): string {
    return this.dateFields.find((f) => f.key === this.dateField)?.icon ?? 'event';
  }

  get dateFieldLabel(): string {
    return this.dateFields.find((f) => f.key === this.dateField)?.label ?? 'Order date';
  }

  // Switching what the range applies to re-runs the filter straight away
  // when a range is already set, so the effect is visible immediately.
  setDateField(field: 'ORDER' | 'PAID' | 'VALIDATED' | 'DISPATCH_BY'): void {
    if (this.dateField === field) return;
    this.dateField = field;
    if (this.filterDtFrom || this.filterDtTo) this.applyFilters();
  }

  applyDatePreset(preset: string): void {
    this.datePreset = preset;
    const today = new Date();
    let from = new Date(today);
    let to = new Date(today);

    switch (preset) {
      case '1d':
        break;
      case '7d':
        from.setDate(today.getDate() - 6);
        break;
      case '30d':
        from.setDate(today.getDate() - 29);
        break;
      case '90d':
        from.setDate(today.getDate() - 89);
        break;
      case 'month':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'ytd':
        from = new Date(today.getFullYear(), 0, 1);
        break;
    }

    this.filterDtFrom = formatDate(from, 'yyyy-MM-dd', 'en');
    this.filterDtTo = formatDate(to, 'yyyy-MM-dd', 'en');
  }

  onCustomDateChange(): void {
    this.datePreset = null;
  }

  toggleMethodFilter(value: string) {
    const i = this.filterDeliveryMethods.indexOf(value);
    if (i === -1) this.filterDeliveryMethods.push(value);
    else this.filterDeliveryMethods.splice(i, 1);
  }

  toggleDispatchGroupFilter(value: string) {
    const i = this.filterDispatchGroups.indexOf(value);
    if (i === -1) this.filterDispatchGroups.push(value);
    else this.filterDispatchGroups.splice(i, 1);
  }

  get methodFilterLabel(): string {
    if (!this.filterDeliveryMethods.length) return 'All Methods';
    if (this.filterDeliveryMethods.length === 1) {
      return this.deliveryMethodOptions.find((m) => m.value === this.filterDeliveryMethods[0])?.label || '1 selected';
    }
    return `${this.filterDeliveryMethods.length} selected`;
  }

  get dispatchGroupFilterLabel(): string {
    if (!this.filterDispatchGroups.length) return 'All Groups';
    if (this.filterDispatchGroups.length === 1) {
      return this.dispatchGroupFilterOptions.find((g) => g.value === this.filterDispatchGroups[0])?.label || '1 selected';
    }
    return `${this.filterDispatchGroups.length} selected`;
  }

  updateQueueList(event: PageEvent): void {
    if (this.queueItemsPerPage !== event.pageSize) {
      this.queueItemsPerPage = event.pageSize || 10;
      this.queuePageNumber = 0;
    } else {
      this.queuePageNumber = event.pageIndex;
    }
    this.loadQueue(false);
  }

  customerName(order: any): string {
    const address = this.shippingAddress(order);
    if (!address) return '-';
    return `${address.firstName ?? ''} ${address.lastName ?? ''}`.trim() || '-';
  }

  // Prefers the SHIPPING address over BILLING - OrderAddresses holds both,
  // in no guaranteed order.
  shippingAddress(order: any): any {
    const addresses = order.OrderAddresses ?? [];
    return addresses.find((a: any) => a.addressType === 'SHIPPING') || addresses[0] || null;
  }

  // Google Maps search for the delivery address.
  mapsUrl(order: any): string {
    const a = this.shippingAddress(order);
    const query = [a?.address1, a?.address2, a?.city, a?.postCode, a?.country].filter(Boolean).join(', ');
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query);
  }

  shippingPostCode(order: any): string {
    return this.shippingAddress(order)?.postCode || '-';
  }

  // Orders.deliveryMethod is stored as a fixed code (see the same mapping
  // in OrdersService's Odoo sync) - not deliveryType, which is a separate,
  // unrelated field.
  private static readonly DELIVERY_METHOD_LABELS: Record<string, string> = {
    STANDARD: 'Standard',
    EXPRESS: 'Express',
    NEXTDAY: 'Next Day',
    COLLECTION: 'Collection',
  };

  deliveryMethodLabel(order: any): string {
    const method = order.deliveryMethod;
    return DeliveryValidationComponent.DELIVERY_METHOD_LABELS[method] || method;
  }

  deliveryMethodIcon(order: any): string {
    return order.deliveryMethod === 'COLLECTION' ? 'storefront' : 'local_shipping';
  }

  // Why the order is where it is: a one-line headline plus tags for what
  // happened to individual lines (partial / damaged / cancelled /
  // substitute).
  deliveryStatus(order: any): { headline: string; tone: string; tags: { label: string; cls: string }[] } {
    const s = order.deliverySummary;
    const tags: { label: string; cls: string }[] = [];
    if (!s || !s.total) return { headline: 'Not started', tone: 'bg-gray-100 text-gray-500', tags };

    if (s.partial) tags.push({ label: `${s.partial} partial`, cls: 'bg-amber-50 text-amber-700' });
    if (s.damaged) tags.push({ label: `${s.damaged} damaged`, cls: 'bg-red-50 text-red-700' });
    if (s.cancelled) tags.push({ label: `${s.cancelled} cancelled`, cls: 'bg-neutral-100 text-neutral-600' });
    if (s.substitutes) tags.push({ label: `${s.substitutes} substitute`, cls: 'bg-blue-50 text-blue-700' });

    const done = s.total - s.pending - s.discrepancy;
    if (s.discrepancy) {
      return { headline: `Quantity mismatch on ${s.discrepancy} line${s.discrepancy === 1 ? '' : 's'}`, tone: 'bg-red-50 text-red-700', tags };
    }
    if (s.pending === s.total) {
      return { headline: 'Not started', tone: 'bg-gray-100 text-gray-500', tags };
    }
    if (s.pending > 0) {
      return { headline: `In progress - ${done} of ${s.total} lines done`, tone: 'bg-blue-50 text-blue-700', tags };
    }
    if (s.validated + s.partial > 0) {
      return { headline: 'All lines checked - awaiting order status', tone: 'bg-green-50 text-green-700', tags };
    }
    return { headline: 'Nothing shipped', tone: 'bg-neutral-100 text-neutral-600', tags };
  }

  deliveryTermsShort(order: any): string | null {
    return formatDeliveryTerms(order.deliveryTerms);
  }

  dispatchDate(order: any): Date | null {
    return computeDispatchDate(order);
  }

  dispatchUrgencyClass(order: any): string {
    return dispatchUrgencyClass(this.dispatchDate(order));
  }

  // dispatchGroup now comes back inline with each order from /queue - no
  // separate bulk lookup needed.
  dispatchGroupOptionFor(order: any): DispatchGroupOption | null {
    return dispatchGroupOption(order.dispatchGroup?.dispatchGroup);
  }

  assignDispatchGroup(order: any, value: string) {
    this.deliveryValidationService.setDispatchGroup(order.orderId, value).subscribe({
      next: (group) => {
        order.dispatchGroup = group;
        this.alertService.alert('Dispatch group updated', 'success');
      },
      error: (error) => {
        this.alertService.alert(error?.error?.message || 'Failed to update dispatch group', 'error');
      },
    });
  }

  openOrder(order: any) {
    this.deliveryValidationService.rememberOrder(order.orderId, order.orderNo);
    this.deliveryValidationService.queueContext = {
      status: 'PROCESSING',
      tab: this.activeTab as QueueTab,
      pageIndex: 0,
      pageSize: 0,
      search: this.filterSearch || undefined,
      orderNo: this.filterOrderNo || undefined,
      postCode: this.filterPostCode || undefined,
      deliveryMethod: this.filterDeliveryMethods.join(',') || undefined,
      dispatchGroup: this.filterDispatchGroups.join(',') || undefined,
      dtFrom: this.filterDtFrom || undefined,
      dtTo: this.filterDtTo || undefined,
      dateField: this.dateField,
    };
    this.router.navigate(['/delivery-validation', order.orderNo]);
  }
}
